// MovieDetail.jsx
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useLocation, Link } from 'react-router-dom';
import { supabaseShowTimes, supabaseMovies } from './supabase';
import { THEATERS, getTicketLink } from './constants';


// Add this helper function at the top of the file, outside the component
const createGoogleCalendarLink = (movieTitle, theater, startTime) => {
  // Create a date object from the startTime
  const eventDate = new Date(startTime);
  // Set end time to 3 hours after start (typical movie length)
  const endDate = new Date(eventDate.getTime() + (3 * 60 * 60 * 1000));
  
  // Format dates for Google Calendar URL
  const startDateTime = eventDate.toISOString().replace(/[:-]/g, '').replace('.000', 'Z');
  const endDateTime = endDate.toISOString().replace(/[:-]/g, '').replace('.000', 'Z');
  
  // Create event details
  const eventDetails = {
    action: 'TEMPLATE',
    text: `${movieTitle} at ${theater}`,
    details: `Movie showing of ${movieTitle} at ${theater}`,
    location: theater,
    dates: `${startDateTime}/${endDateTime}`
  };
  
  // Construct the URL
  const baseUrl = 'https://calendar.google.com/calendar/render';
  const queryString = Object.entries(eventDetails)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
    
  return `${baseUrl}?${queryString}`;
};

function MovieDetail() {
  const { movieId } = useParams();
  const location = useLocation();
  
  // Try to get state from location, window opener, or sessionStorage (for mobile)
  const getMovieState = () => {
    // First try location state
    if (location.state) return location.state;
    
    // Then try window opener (desktop)
    if (window.opener && window.opener.movieState) return window.opener.movieState;
    
    // Finally try sessionStorage (mobile)
    try {
      const sessionState = sessionStorage.getItem(`movieState_${movieId}`);
      if (sessionState) {
        const parsed = JSON.parse(sessionState);
        // Clear it after reading to avoid memory issues
        sessionStorage.removeItem(`movieState_${movieId}`);
        return parsed;
      }
    } catch (e) {
      console.warn('Error reading movieState from sessionStorage:', e);
    }
    
    return null;
  };
  
  const state = getMovieState();
  console.log({location, state})
  
  const [movie, setMovie] = useState(null);
  const [showtimes, setShowtimes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // Holds moderation_info.customTitle from the Supabase movies row so the
  // admin "Edit Title" change on the calendar propagates here too. Without
  // this, the detail page reads only the IST API title and ignores edits.
  const [customTitle, setCustomTitle] = useState(null);

  useEffect(() => {

    async function fetchMovieDetails() {
      try {
        // SUPABASE is the source of truth (2026-07-13). The old IST API read 404'd for
        // every placeholder id (ifc-…, bam-…) — broken /movie/:id pages — and IST is
        // retired from the client entirely. The IST-only trailer embed gracefully
        // no-ops (movie.trailers is simply absent on Supabase records).
        const [supaMovie] = await supabaseMovies.getById(movieId);
        if (supaMovie) {
          // release_dates may be stored as a JSON string — the render expects an object.
          if (typeof supaMovie.release_dates === 'string') {
            try { supaMovie.release_dates = JSON.parse(supaMovie.release_dates); } catch { supaMovie.release_dates = null; }
          }
          setMovie(supaMovie);
          if (supaMovie.moderation_info?.customTitle) setCustomTitle(supaMovie.moderation_info.customTitle);
          document.querySelector('title').innerText = supaMovie.title;
        } else {
          console.error("Failed to fetch movie details");
        }
      } catch (error) {
        console.error("Failed to fetch movie details", error)
      }
    }

    async function fetchShowtimes() {
      try {
        // Use the passed showtimes if available
        if (state?.showtimes) {
          setShowtimes(state.showtimes);
        } else {
          // Fetch showtimes from database when no state is provided
          console.log('Fetching showtimes from database for movie:', movieId);
          const movieShowtimes = await supabaseShowTimes.getByMovieId(movieId);
          setShowtimes(movieShowtimes || []);
        }
      } catch (error) {
        console.error("Failed to fetch showtimes", error);
        setShowtimes([]);
      } finally {
        setIsLoading(false);
      }
    }

    fetchMovieDetails();
    fetchShowtimes();
  }, [movieId, state]);

  // Display title prefers admin-edited customTitle (set on the calendar)
  // and falls back to whatever the IST API gave us. Computed lazily once
  // movie is loaded.
  const displayTitle = customTitle || movie?.title;

  // Group showtimes by date
  const showtimesByDate = showtimes.reduce((acc, showtime) => {
    const date = new Date(showtime.start_at).toLocaleDateString();
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(showtime);
    return acc;
  }, {});

  if (!movie || isLoading) {
    return <div>Loading movie details…</div>;
  }

  const trailerSrc = movie.trailers && movie.trailers.length > 0 && movie.trailers[0].trailer_files && movie.trailers[0].trailer_files[0].url.replace("watch?v=", "embed/");

  // A date-only release is a calendar year, not an instant in local time.
  const releaseDate = Object.values(movie.release_dates || {}).flat().map(r => r?.date).filter(Boolean).sort()[0];
  const releaseYear = Number(movie.release_year) || Number(String(releaseDate || '').slice(0, 4)) || null;

  // Generate Event Schema for all showtimes (enhanced with workPresented)
  const eventSchemas = showtimes.map(showtime => ({
    "@context": "https://schema.org",
    "@type": "ScreeningEvent",
    "name": `${displayTitle} Screening`,
    "startDate": showtime.start_at,
    "location": {
      "@type": "MovieTheater",
      "name": showtime.theater
    },
    "workPresented": {
      "@type": "Movie",
      "name": displayTitle,
      ...(releaseYear && { "dateCreated": String(releaseYear) }),
      ...(movie.synopsis && { "description": movie.synopsis.slice(0, 300) })
    },
    ...(getTicketLink(showtime.booking_link, THEATERS.find(t => t.name === showtime.theater)?.id) && {
      "offers": {
        "@type": "Offer",
        "url": getTicketLink(showtime.booking_link, THEATERS.find(t => t.name === showtime.theater)?.id),
        "availability": "https://schema.org/InStock"
      }
    })
  }));

  // Breadcrumb schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://filmrevivalnyc.com/" },
      { "@type": "ListItem", "position": 2, "name": displayTitle, "item": `https://filmrevivalnyc.com/movie/${movieId}` }
    ]
  };

  // Video schema for trailer
  const trailerYoutubeId = trailerSrc ? trailerSrc.split('/embed/')[1]?.split('?')[0] : null;
  const videoSchema = trailerYoutubeId ? {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": `${displayTitle} Trailer`,
    "description": `Official trailer for ${displayTitle}${releaseYear ? ` (${releaseYear})` : ''}`,
    "thumbnailUrl": `https://img.youtube.com/vi/${trailerYoutubeId}/hqdefault.jpg`,
    "embedUrl": trailerSrc,
    ...(releaseYear && { "uploadDate": `${releaseYear}-01-01` })
  } : null;

  const canonicalUrl = `https://filmrevivalnyc.com${movie.slug ? `/films/${movie.slug}` : `/movie/${movieId}`}`;
  const metaTitle = `${displayTitle}${releaseYear ? ` (${releaseYear})` : ''} Showtimes | Film Revival NYC`;
  const metaDescription = `Showtimes for ${displayTitle}${releaseYear ? ` (${releaseYear})` : ''} at NYC revival cinemas. Find screening times, theaters, and tickets.`;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={`${displayTitle} — Film Revival NYC`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://filmrevivalnyc.com/FILM_REVIVAL_NY_OG.jpg" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${displayTitle} — Film Revival NYC`} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content="https://filmrevivalnyc.com/FILM_REVIVAL_NY_OG.jpg" />
        <link rel="canonical" href={canonicalUrl} />

        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>

        {/* Video Schema for trailer */}
        {videoSchema && (
          <script type="application/ld+json">
            {JSON.stringify(videoSchema)}
          </script>
        )}

        {/* Event Schema structured data */}
        {eventSchemas.map((schema, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>
      <div className="movie-detail">
        <div style={{ 
          textAlign: 'left', 
          marginBottom: '20px',
          position: 'relative'
        }}>
          <a href="/" style={{ 
            textDecoration: 'none', 
            color: '#007bff',
            fontSize: '14px',
            display: 'inline-block'
          }}>
            ← return to homepage
          </a>
        </div>
        
        <h2>{displayTitle}</h2>
        {movie.release_dates ? (
          <p className="release-date">
            Oldest Release Date: {new Date(
              Object.values(movie.release_dates)  // Get arrays of releases for each country
                .flat()  // Flatten into a single array of release objects
                .map(release => release.date)  // Extract just the dates
                .sort((a, b) => new Date(a) - new Date(b))[0]  // Sort and take the earliest
            ).toLocaleDateString()}
          </p>
        ) : (
          <p className="release-date">Release date not available</p>
        )}
        {movie.synopsis ? <p>{movie.synopsis}</p> : <p><em>No synopsis available.</em></p>}
        {trailerSrc && (
          <div className="trailer-container">
            <iframe 
              width="560" 
              height="315" 
              src={trailerSrc}
              title={`${displayTitle} Trailer`} 
              frameBorder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowFullScreen 
            />
          </div>
        )}
        
        <h3>Upcoming Showtimes:</h3>
        {Object.entries(showtimesByDate).length > 0 ? (
          Object.entries(showtimesByDate)
            .sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB))
            .map(([date, dateShowtimes]) => (
              <div key={date} className="date-showtimes">
                <h4>
                  {new Date(date).toLocaleDateString('en-US', { weekday: 'long' })}, {date}
                </h4>
                <ul className="showtimes-list">
                  {dateShowtimes
                    .sort((a, b) => new Date(a.start_at) - new Date(b.start_at))
                    .map(st => {
                      const time = new Date(st.start_at);
                      const timeLabel = time.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
                      const googleCalendarLink = createGoogleCalendarLink(displayTitle, st.theater, st.start_at);
                      const theater = THEATERS.find(t => t.name === st.theater);
                      
                      return (
                        <li key={st.start_at + st.theater}>
                          <strong>{timeLabel}</strong> – {theater ? (
                            <Link 
                              to={`/theater/${theater.slug}`}
                              style={{ 
                                textDecoration: 'none', 
                                color: '#007bff',
                                fontWeight: '500'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                            >
                              {st.theater}
                            </Link>
                          ) : st.theater} –{" "}
                          <span className="action-buttons">
                            {getTicketLink(st.booking_link, theater?.id) ? (
                              <a
                                href={getTicketLink(st.booking_link, theater?.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="action-button booking-link"
                              >
                                Tickets
                              </a>
                            ) : (
                              <em>No booking link</em>
                            )}
                            <a 
                              href={googleCalendarLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="action-button calendar-link"
                            >
                              Add to Calendar
                            </a>
                          </span>
                        </li>
                      );
                    })}
                </ul>
              </div>
            ))
        ) : (
          <p><em>No upcoming showtimes available.</em></p>
        )}
      </div>
    </>
    
  );
}

export default MovieDetail;